import { useEffect, useRef } from 'react';
import { useAuth } from '@/auth/auth-provider';
import { useWorkspace } from '@/hooks/use-workspace';
import { getHostName } from '@/tools/office-tools-registry';
import {
  setDatadogUser,
  clearDatadogUser,
  setDatadogGlobalContext,
  removeDatadogGlobalContext,
  addDatadogAction,
} from '@/services/datadog';

/**
 * Syncs user identity and context to Datadog RUM.
 * Mirrors hai_assistants/ui/providers/datadog-rum-user-provider.tsx.
 *
 * Sets global context:
 *   - office_host (Word/Excel/PowerPoint)
 *   - workspace_id
 *   - platform: 'office-addin'
 *
 * Tracks actions:
 *   - workspace_resolved (when workspace is first set up)
 */
export function DatadogUserProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { workspace } = useWorkspace();
  const prevWorkspaceIdRef = useRef<string | null>(null);

  // Sync user identity
  useEffect(() => {
    if (isAuthenticated && user) {
      setDatadogUser({
        id: user.email,
        email: user.email,
        name: user.name,
      });
      setDatadogGlobalContext('platform', 'office-addin');
      setDatadogGlobalContext('office_host', getHostName());
    } else {
      clearDatadogUser();
      removeDatadogGlobalContext('platform');
      removeDatadogGlobalContext('office_host');
      removeDatadogGlobalContext('workspace_id');
      prevWorkspaceIdRef.current = null;
    }
  }, [isAuthenticated, user]);

  // Sync workspace context
  useEffect(() => {
    if (workspace?.id) {
      setDatadogGlobalContext('workspace_id', workspace.id);

      if (prevWorkspaceIdRef.current !== null && workspace.id !== prevWorkspaceIdRef.current) {
        addDatadogAction('workspace_switch', {
          from_workspace_id: prevWorkspaceIdRef.current,
          to_workspace_id: workspace.id,
        });
      }
      prevWorkspaceIdRef.current = workspace.id;
    } else {
      removeDatadogGlobalContext('workspace_id');
    }
  }, [workspace?.id]);

  return <>{children}</>;
}
